import React, { useEffect, useState, useCallback } from "react";
import "./Calendar.css";

const API_BASE =
    process.env.REACT_APP_API_BASE ??
    (process.env.NODE_ENV === "production"
        ? ""
        : "http://localhost:5000");

const getToken = () => localStorage.getItem("token");

const emptyForm = {
    date: "",
    type: "event",
    title: "",
    description: "",
    location: "",
    start_time: "",
    end_time: "",
    reminder: false,
    reminder_days: 0,
};

const responseStatusLabel = (status) => {
    if (status === "attend") return "CONFIRMED";
    if (status === "maybe") return "MAYBE";
    if (status === "decline") return "DECLINED";
    return String(status || "").toUpperCase();
};


export default function AdminCalendar() {
    const START_YEAR = 2026;
    const [year, setYear] = useState(START_YEAR);

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ ...emptyForm });
    const [showBirthSurvive, setShowBirthSurvive] = useState(false);
    const [responseEvent, setResponseEvent] = useState(null);
    const [responseRows, setResponseRows] = useState([]);
    const [responsesLoading, setResponsesLoading] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);

    const canShowResponses = (row) => ["event", "volunteer", "occasion"].includes(row?.type);

    const loadResponses = async (row) => {
        setResponseEvent(row);
        setResponsesLoading(true);
        setResponseRows([]);

        try {
            const res = await fetch(`${API_BASE}/api/calendar/${row.id}/responses`, {
                headers: { Authorization: `Bearer ${getToken()}` },
            });
            const data = await res.json();
            if (res.ok && data.ok) {
                setResponseRows(data.rows || []);
            }
        } catch (err) {
            console.error("Calendar responses load error:", err);
        } finally {
            setResponsesLoading(false);
        }
    };

    const deleteResponse = async (row) => {
        if (!responseEvent) return;
        if (!window.confirm(`Delete response for ${row.firstname} ${row.lastname}?`)) return;

        try {
            const res = await fetch(
                `${API_BASE}/api/calendar/${responseEvent.id}/responses/${encodeURIComponent(row.primaryid)}`,
                {
                    method: "DELETE",
                    headers: { Authorization: `Bearer ${getToken()}` },
                }
            );
            const data = await res.json();

            if (!res.ok || !data.ok) {
                alert(data?.error || "Delete response failed.");
                return;
            }

            setResponseRows((prev) =>
                prev.filter((item) => item.primaryid !== row.primaryid)
            );

            setResponseEvent((prev) =>
                prev
                    ? {
                        ...prev,
                        attending_count: data.counts?.attending_count ?? prev.attending_count,
                        maybe_count: data.counts?.maybe_count ?? prev.maybe_count,
                        declined_count: data.counts?.declined_count ?? prev.declined_count,
                    }
                    : prev
            );

            setRows((prev) =>
                prev.map((item) =>
                    item.id === responseEvent.id
                        ? {
                            ...item,
                            attending_count: data.counts?.attending_count ?? item.attending_count,
                            maybe_count: data.counts?.maybe_count ?? item.maybe_count,
                            declined_count: data.counts?.declined_count ?? item.declined_count,
                        }
                        : item
                )
            );
        } catch (err) {
            console.error("Delete response error:", err);
            alert("Network error while deleting response.");
        }
    };

    /* ================= SEARCH / SORT / PAGINATION ================= */

    const [search, setSearch] = useState("");
    const [sortBy, setSortBy] = useState("date");
    const [sortDir, setSortDir] = useState("asc");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const toggleSort = (field) => {
        if (sortBy === field) {
            setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        } else {
            setSortBy(field);
            setSortDir("asc");
        }
    };

    /* ================= LOAD ================= */

    const loadCalendar = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(
                `${API_BASE}/api/calendar?year=${year}`,
                {
                    headers: {
                        Authorization: `Bearer ${getToken()}`,
                    },
                }
            );

            const data = await res.json();
            if (res.ok && data.ok) {
                setRows(data.rows || []);
                setSelectedIds([]);
            }
        } catch (err) {
            console.error("Calendar load error:", err);
        } finally {
            setLoading(false);
        }
    }, [year]);

    useEffect(() => {
        loadCalendar();
    }, [loadCalendar]);

    /* ================= FILTER ================= */

    const filteredRows = rows.filter((r) => {
        if (!showBirthSurvive && r.type === "birth_survive") {
            return false;
        }

        const q = search.toLowerCase();

        return (
            r.date?.toLowerCase().includes(q) ||
            r.type?.toLowerCase().includes(q) ||
            r.title?.toLowerCase().includes(q) ||
            `${r.firstname || ""} ${r.lastname || ""}`
                .toLowerCase()
                .includes(q) ||
            r.location?.toLowerCase().includes(q)
        );
    });

    /* ================= SORT ================= */

    const sortedRows = [...filteredRows].sort((a, b) => {
        let A =
            sortBy === "title"
                ? a.title || `${a.firstname || ""} ${a.lastname || ""}`
                : a[sortBy] || "";

        let B =
            sortBy === "title"
                ? b.title || `${b.firstname || ""} ${b.lastname || ""}`
                : b[sortBy] || "";

        if (sortBy === "date") {
            return sortDir === "asc"
                ? new Date(A) - new Date(B)
                : new Date(B) - new Date(A);
        }

        return sortDir === "asc"
            ? String(A).localeCompare(String(B))
            : String(B).localeCompare(String(A));
    });

    /* ================= PAGINATION ================= */

    const totalPages = Math.ceil(sortedRows.length / pageSize);

    const paginatedRows = sortedRows.slice(
        (page - 1) * pageSize,
        page * pageSize
    );

    const visibleIds = paginatedRows.map((row) => row.id);
    const selectedOnPage = visibleIds.filter((id) => selectedIds.includes(id));
    const allVisibleSelected =
        visibleIds.length > 0 && selectedOnPage.length === visibleIds.length;

    const toggleSelectRow = (id) => {
        setSelectedIds((prev) =>
            prev.includes(id)
                ? prev.filter((selectedId) => selectedId !== id)
                : [...prev, id]
        );
    };

    const toggleSelectVisible = () => {
        setSelectedIds((prev) => {
            if (allVisibleSelected) {
                return prev.filter((id) => !visibleIds.includes(id));
            }

            return Array.from(new Set([...prev, ...visibleIds]));
        });
    };

    /* ================= IMPORT BIRTH / SURVIVE ================= */

    const importBirthSurvive = async () => {
        if (!window.confirm(`Import Birth/Survive for year ${year}?`)) return;

        try {
            const res = await fetch(
                `${API_BASE}/api/calendar/import-birth-survive`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${getToken()}`,
                    },
                    body: JSON.stringify({ year }),
                }
            );

            const data = await res.json();

            if (!res.ok || !data.ok) {
                alert(data.error || "Import failed");
                return;
            }

            alert(`✅ Imported ${data.inserted} records`);
            loadCalendar();
        } catch (err) {
            console.error("Import error:", err);
            alert("Network error");
        }
    };

    /* ================= SAVE ================= */

    const saveEvent = async () => {
        if (!form.date || !form.type) {
            alert("Date and Type are required");
            return;
        }

        try {
            const res = await fetch(
                editing
                    ? `${API_BASE}/api/calendar/${editing.id}`
                    : `${API_BASE}/api/calendar`,
                {
                    method: editing ? "PUT" : "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${getToken()}`,
                    },
                    body: JSON.stringify({ year, ...form }),
                }
            );

            const data = await res.json();

            if (!res.ok || !data.ok) {
                alert(data.error || "Save failed");
                return;
            }

            setShowForm(false);
            setEditing(null);
            setForm({ ...emptyForm });
            loadCalendar();
        } catch (err) {
            console.error("Save calendar error:", err);
            alert("Network error");
        }
    };

    /* ================= DELETE ================= */

    const formatDateOnly = (dateStr) => {
        if (!dateStr) return "";

        // strip time completely
        const [y, m, d] = dateStr.split("T")[0].split("-");

        return new Date(y, m - 1, d).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
        });
    };

    const deleteEvent = async (row) => {
        if (
            !window.confirm(
                `Are you sure you want to delete the event on ${formatDateOnly(row.date)}?`
            )
        ) {
            return;
        }

        try {
            await fetch(`${API_BASE}/api/calendar/${row.id}`, {
                method: "DELETE",
                headers: {
                    Authorization: `Bearer ${getToken()}`,
                },
            });
            loadCalendar();
        } catch (err) {
            console.error("Delete error:", err);
        }
    };

    const deleteSelectedEvents = async () => {
        if (selectedIds.length === 0) return;

        if (
            !window.confirm(
                `Delete ${selectedIds.length} selected calendar event${selectedIds.length === 1 ? "" : "s"}?`
            )
        ) {
            return;
        }

        try {
            await Promise.all(
                selectedIds.map((id) =>
                    fetch(`${API_BASE}/api/calendar/${id}`, {
                        method: "DELETE",
                        headers: {
                            Authorization: `Bearer ${getToken()}`,
                        },
                    })
                )
            );

            setSelectedIds([]);
            loadCalendar();
        } catch (err) {
            console.error("Bulk calendar delete error:", err);
            alert("Network error while deleting selected events.");
        }
    };

    const totalEventCount = filteredRows.filter(
        (r) => r.type !== "birth_survive"
    ).length;

    const END_YEAR = new Date().getFullYear() + 5;

    /* ================= RENDER ================= */

    return (
        <div className="calendar-admin">
            {/* HEADER */}
            <div className="calendar-events-header">
                <span>📅</span>
                <span>Calendar Events</span>
            </div>

            {/* TOP BAR */}
            <div className="calendar-header">
                <div className="calendar-header-left">
                    <select
                        value={year}
                        onChange={(e) => setYear(Number(e.target.value))}
                    >
                        {Array.from(
                            { length: END_YEAR - START_YEAR + 1 },
                            (_, i) => START_YEAR + i
                        ).map((y) => (
                            <option key={y} value={y}>
                                {y}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="calendar-header-right">
                    <button
                        className="btn-secondary calendar-tooltip"
                        onClick={importBirthSurvive}
                        data-tooltip="Import Triskelion Birth"
                    >
                        📥 Import
                    </button>

                    <button
                        className="btn-primary"
                        onClick={() => {
                            setEditing(null);
                            setForm({ ...emptyForm });
                            setShowForm(true);
                        }}
                    >
                        ➕ Add Event
                    </button>
                </div>
            </div>

            {/* TABLE */}
            <div className="calendar-table">
                {/* CONTROLS */}
                <div className="table-controls">
                    <select
                        value={pageSize}
                        onChange={(e) => {
                            setPageSize(Number(e.target.value));
                            setPage(1);
                        }}
                    >
                        <option value={10}>10 | Show entries</option>
                        <option value={25}>25 | Show entries</option>
                        <option value={50}>50 | Show entries</option>
                    </select>

                    <label className="toggle-birth">
                        <input
                            type="checkbox"
                            checked={showBirthSurvive}
                            onChange={(e) => {
                                const checked = e.target.checked;
                                setShowBirthSurvive(checked);

                                if (!checked) {
                                    const birthSurviveIds = rows
                                        .filter((row) => row.type === "birth_survive")
                                        .map((row) => row.id);

                                    setSelectedIds((prev) =>
                                        prev.filter((id) => !birthSurviveIds.includes(id))
                                    );
                                }
                            }}
                        />
                        <span>Show Birth / Survive</span>
                    </label>

                    <div className="table-center">
                        Total Events {totalEventCount} entries
                    </div>

                    <input
                        placeholder="Search…"
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setPage(1);
                        }}
                    />
                </div>

                {loading ? (
                    <div className="muted">Loading…</div>
                ) : filteredRows.length === 0 ? (
                    <div className="muted">No events for {year}</div>
                ) : (
                    <>
                        {selectedIds.length > 0 && (
                            <div className="bulk-delete-bar">
                                <span>{selectedIds.length} selected</span>
                                <button
                                    type="button"
                                    className="btn-danger"
                                    onClick={deleteSelectedEvents}
                                >
                                    Delete Selected
                                </button>
                            </div>
                        )}

                        <div className="table-wrapper">
                            <div className="table-scroll">
                                <table className="nice-table">
                                    <thead>
                                        <tr>
                                            <th className="calendar-select-col">
                                                <label
                                                    className="calendar-select-all"
                                                    title="Select All"
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={allVisibleSelected}
                                                        onChange={toggleSelectVisible}
                                                    />
                                                    <span>All</span>
                                                </label>
                                            </th>
                                            <th onClick={() => toggleSort("date")}>Date</th>
                                            <th onClick={() => toggleSort("type")}>Type</th>
                                            <th onClick={() => toggleSort("title")}>Title / Name</th>
                                            <th>Location</th>
                                            <th>Time</th>
                                            <th>Responses</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {paginatedRows.map((r) => {
                                            const isWeekend = [0, 6].includes(
                                                new Date(r.date).getDay()
                                            );

                                            return (
                                                <tr key={r.id}>
                                                    <td
                                                        data-label="Select"
                                                        className="calendar-select-col"
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedIds.includes(r.id)}
                                                            onChange={() => toggleSelectRow(r.id)}
                                                        />
                                                    </td>
                                                    <td
                                                        data-label="Date"
                                                        className={isWeekend ? "weekend" : ""}
                                                    >
                                                        {r.date
                                                            ? r.date.split("T")[0]
                                                            : "—"}
                                                    </td>

                                                    <td data-label="Type">
                                                        {r.type
                                                            .replace("_", " ")
                                                            .toUpperCase()}
                                                    </td>

                                                    <td
                                                        data-label="Title / Name"
                                                        className={isWeekend ? "weekend" : ""}
                                                    >
                                                        {r.title ||
                                                            `${r.firstname || ""} ${r.lastname || ""}`}
                                                    </td>

                                                    <td data-label="Location">
                                                        {r.location || "—"}
                                                    </td>

                                                    <td data-label="Time">
                                                        {r.start_time
                                                            ? `${r.start_time} - ${r.end_time || ""}`
                                                            : "—"}
                                                    </td>

                                                    <td data-label="Responses">
                                                        {canShowResponses(r) ? (
                                                            <button
                                                                className="btn-primary"
                                                                onClick={() => loadResponses(r)}
                                                            >
                                                                Confirmed {r.attending_count || 0} | Maybe {r.maybe_count || 0} | Decline {r.declined_count || 0}
                                                            </button>
                                                        ) : (
                                                            "—"
                                                        )}
                                                    </td>

                                                    <td data-label="Actions">
                                                        <button
                                                            className="btn-secondary"
                                                            onClick={() => {
                                                                setEditing(r);
                                                                setForm({
                                                                    date: r.date
                                                                        ? r.date.split("T")[0]
                                                                        : "",
                                                                    type: r.type,
                                                                    title: r.title || "",
                                                                    description:
                                                                        r.description || "",
                                                                    location: r.location || "",
                                                                    start_time:
                                                                        r.start_time || "",
                                                                    end_time:
                                                                        r.end_time || "",
                                                                    reminder: r.reminder,
                                                                    reminder_days:
                                                                        r.reminder_days || 0,
                                                                });
                                                                setShowForm(true);
                                                            }}
                                                        >
                                                            Edit
                                                        </button>

                                                        <button
                                                            className="btn-danger"
                                                            onClick={() => deleteEvent(r)}
                                                        >
                                                            Delete
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* PAGINATION */}
                        <div className="table-footer">
                            <div className="pagination">
                                <button
                                    disabled={page === 1}
                                    onClick={() => setPage(page - 1)}
                                >
                                    ◀
                                </button>

                                <span className="page-pill">
                                    Page {page} of {totalPages || 1}
                                </span>

                                <button
                                    disabled={page === totalPages}
                                    onClick={() => setPage(page + 1)}
                                >
                                    ▶
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* MODAL */}
            {showForm && (
                <div
                    className="modal-overlay"
                    onClick={() => setShowForm(false)}
                >
                    <div
                        className="modal-box"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h2
                            style={{
                                marginBottom: "4px",
                                color: "darkgoldenrod",
                            }}
                        >
                            Event Date Configuration
                        </h2>

                        <h3>{editing ? "Edit Event" : "Add Event"}</h3>

                        <div className="modal-grid">
                            <input
                                type="date"
                                value={form.date}
                                onChange={(e) =>
                                    setForm((p) => ({
                                        ...p,
                                        date: e.target.value,
                                    }))
                                }
                            />

                            <select
                                value={form.type}
                                onChange={(e) =>
                                    setForm((p) => ({
                                        ...p,
                                        type: e.target.value,
                                    }))
                                }
                            >
                                <option value="event">Event</option>
                                <option value="volunteer">Volunteer</option>
                                <option value="occasion">Occasion</option>
                                <option value="holiday">Public Holiday</option>
                                <option value="birth_survive">Birth / Survive</option>
                            </select>

                            <input
                                placeholder="Title / Occasion"
                                value={form.title}
                                onChange={(e) =>
                                    setForm((p) => ({
                                        ...p,
                                        title: e.target.value,
                                    }))
                                }
                            />

                            <input
                                placeholder="Location"
                                value={form.location}
                                onChange={(e) =>
                                    setForm((p) => ({
                                        ...p,
                                        location: e.target.value,
                                    }))
                                }
                            />

                            <input
                                type="time"
                                value={form.start_time}
                                onChange={(e) =>
                                    setForm((p) => ({
                                        ...p,
                                        start_time: e.target.value,
                                    }))
                                }
                            />

                            <input
                                type="time"
                                value={form.end_time}
                                onChange={(e) =>
                                    setForm((p) => ({
                                        ...p,
                                        end_time: e.target.value,
                                    }))
                                }
                            />

                            <textarea
                                placeholder="Description"
                                value={form.description}
                                onChange={(e) =>
                                    setForm((p) => ({
                                        ...p,
                                        description: e.target.value,
                                    }))
                                }
                            />

                            <div className="reminder-box">
                                <label className="reminder-toggle">
                                    <input
                                        type="checkbox"
                                        checked={form.reminder}
                                        onChange={(e) =>
                                            setForm((p) => ({
                                                ...p,
                                                reminder: e.target.checked,
                                            }))
                                        }
                                    />
                                    <span>Enable Reminder</span>
                                </label>

                                {form.reminder && (
                                    <div className="reminder-input">
                                        <input
                                            type="number"
                                            min="0"
                                            placeholder="Days before event"
                                            value={form.reminder_days}
                                            onChange={(e) =>
                                                setForm((p) => ({
                                                    ...p,
                                                    reminder_days: Number(
                                                        e.target.value
                                                    ),
                                                }))
                                            }
                                        />
                                        <small className="reminder-hint">
                                            Reminder will be sent before the event date
                                        </small>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="card-actions">
                            <button
                                className="btn-primary"
                                onClick={saveEvent}
                            >
                                Save
                            </button>

                            <button
                                className="btn-secondary"
                                onClick={() => setShowForm(false)}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {responseEvent && (
                <div
                    className="modal-overlay"
                    onClick={() => setResponseEvent(null)}
                >
                    <div
                        className="modal-box attendee-modal"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h2 style={{ color: "darkgoldenrod" }}>
                            Event Responses
                        </h2>
                        <h3>{responseEvent.title || "Untitled Event"}</h3>

                        <div className="attendee-summary">
                            <span>Confirmed: {responseEvent.attending_count || 0}</span>
                            <span>Maybe: {responseEvent.maybe_count || 0}</span>
                            <span>Declined: {responseEvent.declined_count || 0}</span>
                        </div>

                        {responsesLoading ? (
                            <div className="muted">Loading responses...</div>
                        ) : responseRows.length === 0 ? (
                            <div className="muted">No responses yet.</div>
                        ) : (
                            <div className="attendee-list">
                                {responseRows.map((row) => (
                                    <div key={`${row.primaryid}-${row.updated_at}`} className="attendee-row">
                                        <div>
                                            <strong>{row.firstname} {row.lastname}</strong>
                                            <span>{row.primaryid}</span>
                                        </div>
                                        <div className={`attendee-status ${row.status}`}>
                                            {responseStatusLabel(row.status)}
                                        </div>
                                        <div className="attendee-comment">
                                            {row.comment || "No comment"}
                                        </div>
                                        <div className="attendee-time">
                                            {row.updated_at
                                                ? new Date(row.updated_at).toLocaleString()
                                                : "—"}
                                        </div>
                                        <button
                                            type="button"
                                            className="btn-danger attendee-delete-btn"
                                            onClick={() => deleteResponse(row)}
                                        >
                                            Delete
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="card-actions">
                            <button
                                className="btn-secondary"
                                onClick={() => setResponseEvent(null)}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
